import {
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Stack,
  FormHelperText,
  FormErrorMessage,
  type FormControlProps,
  Tag,
  Box,
} from '@chakra-ui/react';

import type { FormFieldOption } from '@duke-hq/shared-constants/assessments';
import { testIds } from './testIds';

interface RadioGroupFieldProps extends Omit<FormControlProps, 'onChange' | 'value'> {
  id: string;
  label: string;
  options: FormFieldOption[];
  value: string;
  onChange: (value: string) => void;
  helperText?: string;
  error?: string;
  isCorrect?: boolean | null;
}

/**
 * Renders a group of radio buttons within a form control.
 */
export function RadioGroupField({
  id,
  label,
  options,
  value,
  onChange,
  helperText,
  error,
  isCorrect,
  ...formControlProps
}: RadioGroupFieldProps) {
  const isInvalid = error !== undefined && error.length > 0;
  const isInactive =
    (formControlProps.isReadOnly ?? false) || (formControlProps.isDisabled ?? false);

  return (
    <FormControl isInvalid={isInvalid} {...formControlProps} sx={{ display: 'contents' }}>
      <FormLabel htmlFor={id} fontWeight="semibold">
        {typeof isCorrect === 'boolean' && (
          <Tag size="sm" variant="subtle" colorScheme={isCorrect ? 'green' : 'red'} mr={2}>
            {isCorrect ? 'Correct' : 'Incorrect'}
          </Tag>
        )}
        {label}
      </FormLabel>

      <Box gridRow="2" minH={0}>
        {helperText !== undefined && helperText.length > 0 ? (
          <FormHelperText mt={0} mb={1}>
            {helperText}
          </FormHelperText>
        ) : null}
      </Box>

      <Box gridRow="3" mt={1}>
        <RadioGroup
          id={id}
          data-testid={`${testIds.radioGroup}-${id}`}
          onChange={onChange}
          value={value}
          colorScheme="brand"
        >
          <Stack
            direction="column"
            sx={{
              '.chakra-radio': {
                cursor: isInactive ? 'not-allowed' : 'default',
              },
              '.chakra-radio__label': {
                color: isInactive ? 'fg.muted' : 'fg.default',
              },
              '.chakra-radio__control': {
                opacity: isInactive ? 0.8 : 1,
              },
            }}
          >
            {options.map((option) => (
              <Radio
                key={option.id}
                data-testid={`${testIds.radioOption}-${id}-${option.id}`}
                value={option.value}
                isReadOnly={formControlProps.isReadOnly}
                isDisabled={formControlProps.isDisabled}
              >
                {option.label}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      </Box>

      <Box gridRow="4" minH={isInvalid ? 'auto' : 0}>
        {isInvalid ? (
          <FormErrorMessage mt={2} mb={0}>
            {error}
          </FormErrorMessage>
        ) : null}
      </Box>
    </FormControl>
  );
}
